import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, Link } from "wouter";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest } from "@/lib/queryClient";
import { ArrowLeft, ArrowLeftRight, Loader2, Store, ChevronRight } from "lucide-react";
import type { Item } from "@shared/schema";

type PublicUser = { id: string; displayName?: string | null; firstName?: string | null };

type BarterOffer = {
  id: string;
  status: "pending" | "accepted" | "declined" | "withdrawn";
  direction: "sent" | "received";
  message: string | null;
  targetQty: number;
  offeredQty: number;
  createdAt: string;
  targetItem: Item | null;
  offeredItem: Item | null;
  proposer: PublicUser | null;
  recipient: PublicUser | null;
};

const STATUS_LABEL: Record<BarterOffer["status"], string> = {
  pending: "Pending",
  accepted: "Accepted",
  declined: "Declined",
  withdrawn: "Withdrawn",
};

function name(u: PublicUser | null): string {
  return u?.displayName || u?.firstName || "Someone";
}

function OfferCard({ offer, onRespond, pending }: {
  offer: BarterOffer;
  onRespond: (action: string) => void;
  pending: boolean;
}) {
  const theirItem = offer.direction === "received" ? offer.offeredItem : offer.targetItem;
  const yourItem = offer.direction === "received" ? offer.targetItem : offer.offeredItem;
  const counterparty = offer.direction === "received" ? offer.proposer : offer.recipient;

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {offer.direction === "received" ? "From" : "To"} {name(counterparty)}
          </span>
          <span className={offer.status === "accepted" ? "text-green-600 font-medium" : ""}>
            {STATUS_LABEL[offer.status]}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">They give</div>
            <div className="font-medium truncate">{theirItem?.name || "Deleted listing"}</div>
            {offer.direction === "received" && <div className="text-xs text-muted-foreground">Qty {offer.offeredQty}</div>}
          </div>
          <ArrowLeftRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="flex-1 min-w-0 text-right">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">You give</div>
            <div className="font-medium truncate">{yourItem?.name || "Deleted listing"}</div>
            {offer.direction === "received" && <div className="text-xs text-muted-foreground">Qty {offer.targetQty}</div>}
          </div>
        </div>

        {offer.message && <p className="text-sm text-muted-foreground italic">"{offer.message}"</p>}

        {offer.status === "pending" && (
          <div className="flex gap-2 pt-1">
            {offer.direction === "received" ? (
              <>
                <Button size="sm" className="flex-1" disabled={pending} onClick={() => onRespond("accept")}>
                  Accept
                </Button>
                <Button size="sm" variant="outline" className="flex-1" disabled={pending} onClick={() => onRespond("decline")}>
                  Decline
                </Button>
              </>
            ) : (
              <Button size="sm" variant="outline" className="flex-1" disabled={pending} onClick={() => onRespond("withdraw")}>
                Withdraw
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Barter() {
  const [, setLocation] = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!authLoading && !user?.canBarter) setLocation("/");
  }, [authLoading, user, setLocation]);

  const { data: offers = [], isLoading } = useQuery<BarterOffer[]>({
    queryKey: ["/api/barter/offers"],
    enabled: !!user?.canBarter,
  });

  const { data: myItems = [], isLoading: itemsLoading } = useQuery<Item[]>({
    queryKey: ["/api/seller/items"],
    enabled: !!user?.canBarter,
  });

  const respondMutation = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: string }) => {
      const res = await apiRequest("POST", `/api/barter/offers/${id}/respond`, { action });
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/barter/offers"] }),
    onError: (e: any) => toast({ title: "Couldn't update offer", description: e.message, variant: "destructive" }),
  });

  const toggleMutation = useMutation({
    mutationFn: async (item: Item) => {
      const res = await apiRequest("PATCH", `/api/items/${item.id}/barter`, {
        barterEnabled: !item.barterEnabled,
        barterAcceptCategories: item.barterAcceptCategories ?? [],
        barterNote: item.barterNote ?? null,
      });
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/seller/items"] }),
    onError: (e: any) => toast({ title: "Couldn't update listing", description: e.message, variant: "destructive" }),
  });

  if (!user?.canBarter) return null;

  const received = offers.filter((o) => o.direction === "received" && o.status === "pending");
  const sent = offers.filter((o) => o.direction === "sent" && o.status === "pending");
  const resolved = offers.filter((o) => o.status !== "pending");
  const tradingCount = myItems.filter((i) => i.barterEnabled).length;

  return (
    <div className="min-h-screen bg-background pb-24">
      <Header />
      <div className="max-w-2xl mx-auto px-4 py-4 space-y-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => setLocation("/")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5" /> Trades
            </h1>
            <p className="text-sm text-muted-foreground">
              Turn on trading for a listing, then propose swaps on other sellers' listings.
            </p>
          </div>
        </div>

        {/* Your listings — turn trading on so people can offer swaps */}
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-muted-foreground">
            Your listings {tradingCount > 0 && `· ${tradingCount} open to trade`}
          </h2>
          {itemsLoading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : myItems.length === 0 ? (
            <Card>
              <CardContent className="p-4 text-center space-y-3">
                <p className="text-sm text-muted-foreground">List something first to start trading.</p>
                <Button size="sm" onClick={() => setLocation("/seller")}>
                  <Store className="h-4 w-4 mr-2" /> Go to seller dashboard
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0 divide-y">
                {myItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-3 p-3">
                    <Link href={`/items/${item.id}`} className="flex-1 min-w-0">
                      <div className="font-medium truncate">{item.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {item.barterEnabled
                          ? (item.barterAcceptCategories && item.barterAcceptCategories.length > 0
                              ? `Accepting: ${item.barterAcceptCategories.length} categories`
                              : "Open to any trade")
                          : "Not trading"}
                      </div>
                    </Link>
                    <Switch
                      checked={!!item.barterEnabled}
                      disabled={toggleMutation.isPending}
                      onCheckedChange={() => toggleMutation.mutate(item)}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          <p className="text-xs text-muted-foreground px-1">
            Tap a listing to choose exactly what you'll accept in trade.
          </p>
        </section>

        {/* Find something to trade for */}
        <Button variant="outline" className="w-full" onClick={() => setLocation("/")}>
          Browse listings to trade for <ChevronRight className="h-4 w-4 ml-1" />
        </Button>

        {/* Offers */}
        {isLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <>
            {received.length > 0 && (
              <section className="space-y-2">
                <h2 className="text-sm font-semibold text-muted-foreground">Offers to you</h2>
                {received.map((o) => (
                  <OfferCard key={o.id} offer={o} pending={respondMutation.isPending}
                    onRespond={(action) => respondMutation.mutate({ id: o.id, action })} />
                ))}
              </section>
            )}
            {sent.length > 0 && (
              <section className="space-y-2">
                <h2 className="text-sm font-semibold text-muted-foreground">Your pending offers</h2>
                {sent.map((o) => (
                  <OfferCard key={o.id} offer={o} pending={respondMutation.isPending}
                    onRespond={(action) => respondMutation.mutate({ id: o.id, action })} />
                ))}
              </section>
            )}
            {resolved.length > 0 && (
              <section className="space-y-2">
                <h2 className="text-sm font-semibold text-muted-foreground">History</h2>
                {resolved.map((o) => (
                  <OfferCard key={o.id} offer={o} pending={respondMutation.isPending} onRespond={() => {}} />
                ))}
              </section>
            )}
            {offers.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-2">No trade offers yet.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
